import { groupCubeRows, total, usd, type ExecutiveTransaction } from "../../data/executiveOverview";

function median(values: number[]) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function SpendBarChart({ data }: { data: { i: number; value: number }[] }) {
  const svgW = 632;
  const svgH = 160;
  const ml = 28;
  const mr = 4;
  const mt = 4;
  const mb = 4;
  const chartW = svgW - ml - mr;
  const chartH = svgH - mt - mb;
  const maxVal = Math.max(Math.ceil(Math.max(...data.map((d) => d.value), 0) / 10) * 10, 10);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(maxVal * f));
  const n = data.length || 1;
  const slotW = chartW / n;
  const barW = Math.max(slotW * 0.75, 2);

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} className="csip-bar-chart-svg">
      <g transform={`translate(${ml},${mt})`}>
        {yTicks.map((tick) => {
          const y = chartH * (1 - tick / maxVal);
          return (
            <g key={`ytick-${tick}`}>
              <line x1={0} y1={y} x2={chartW} y2={y} stroke="#f0f0f0" strokeWidth={1} />
              <text x={-4} y={y + 3} textAnchor="end" fontSize={9} fill="#9ca3af">{tick}M</text>
            </g>
          );
        })}
        {data.map((d) => {
          const barH = (d.value / maxVal) * chartH;
          const x = d.i * slotW + (slotW - barW) / 2;
          const y = chartH - barH;
          return (
            <rect
              key={`bar-${d.i}`}
              x={x}
              y={y}
              width={barW}
              height={barH}
              fill="#776EA9"
              rx={2}
            />
          );
        })}
      </g>
    </svg>
  );
}

export function ConsolidationStrategyImagePanel({ rows }: { rows: ExecutiveTransaction[] }) {
  const categoryGroups = groupCubeRows(rows, "Category_L2");
  const spendMedian = median(categoryGroups.map((g) => g.amount));
  const supplierMedian = median(categoryGroups.map((g) => g.suppliers));

  type CategoryGroup = (typeof categoryGroups)[number];
  const quadrants: Record<"mustChase" | "highSaving" | "lowPotential" | "safeHeaven", CategoryGroup[]> = {
    mustChase: [], highSaving: [], lowPotential: [], safeHeaven: [],
  };
  for (const g of categoryGroups) {
    const highSpend = g.amount >= spendMedian;
    const manySuppliers = g.suppliers >= supplierMedian;
    const key = highSpend && manySuppliers ? "highSaving" : !highSpend && manySuppliers ? "mustChase" : !highSpend && !manySuppliers ? "lowPotential" : "safeHeaven";
    quadrants[key].push(g);
  }
  for (const list of Object.values(quadrants)) list.sort((first, second) => second.amount - first.amount);
  const rowLabel = (g: CategoryGroup) => `${g.suppliers}-${usd(g.amount)}`;

  const supplierGroups = groupCubeRows(rows, "Parent_Supplier");
  const totalSpend = total(rows, "Spend_Amount_USD") || 1;
  let cumulative = 0;
  let highSpendCount = 0;
  for (const g of supplierGroups) {
    if (cumulative / totalSpend >= 0.8) break;
    cumulative += g.amount;
    highSpendCount += 1;
  }
  const highSpendGroups = supplierGroups.slice(0, highSpendCount);
  const tailGroups = supplierGroups.slice(highSpendCount);
  const highSpendSum = highSpendGroups.reduce((sum, g) => sum + g.amount, 0);
  const tailSum = tailGroups.reduce((sum, g) => sum + g.amount, 0);
  const highSpendPct = Math.round((highSpendSum / totalSpend) * 100);
  const barData = supplierGroups.map((g, i) => ({ i, value: g.amount / 1_000_000 }));

  const quadrantList: { key: keyof typeof quadrants; title: string; sub: string; modifier: string }[] = [
    { key: "mustChase", title: "Must Chase", sub: "Low spend- Many suppliers", modifier: "must-chase" },
    { key: "highSaving", title: "High Saving Potential", sub: "High spend- Many suppliers", modifier: "high-saving" },
    { key: "lowPotential", title: "Low Potential", sub: "Low spend- Few suppliers", modifier: "low-potential" },
    { key: "safeHeaven", title: "Safe Heaven", sub: "High spend- Few suppliers", modifier: "safe-heaven" },
  ];

  return (
    <div className="csip-panel">
      <div className="csip-main-grid">
        {/* Matrix */}
        <div className="bg-white pad-12 radius-10">
          <div className="csip-panel-title">
            Spend vs Supplier Count Matrix (Fishbone Categories)
          </div>
          <div className="csip-matrix-grid">
            {quadrantList.map((q) => (
              <div key={q.key} className={`csip-quadrant csip-quadrant--${q.modifier}`}>
                <div className="csip-quadrant-header">
                  <span className="csip-quadrant-title">{q.title}</span>
                  <span className="csip-quadrant-count">{quadrants[q.key].length}</span>
                </div>
                <div className={`csip-quadrant-sub csip-quadrant-sub--${q.modifier}`}>{q.sub}</div>
                {quadrants[q.key].slice(0, 7).map((g) => (
                  <div key={g.label} className="csip-quadrant-row">
                    <span className="csip-row-cat" title={g.label}>{g.label}</span>
                    <span className="csip-row-val">{rowLabel(g)}</span>
                  </div>
                ))}
                {!quadrants[q.key].length && (
                  <div className="csip-quadrant-row"><span className="csip-row-cat">No categories match the selected filters.</span></div>
                )}
              </div>
            ))}
          </div>

          {/* Axis label */}
          <div className="csip-axis-label">SPEND →</div>

          {/* Insight */}
          <div className="csip-insight csip-insight--spaced">
            <span className="csip-insight-text">
              <strong>Insight:</strong> Click any category to drill into its suppliers · Top-Left = Must Chase · Top-Right = High Saving Potential · Bottom-Left = Low Potential · Bottom-Right = Safe Heaven
            </span>
          </div>
        </div>

        {/* Right panel */}
        <div className="bg-white pad-12 radius-10">
          <div className="csip-panel-title">
            Spend vs Supplier Count Matrix (Fishbone Categories)
          </div>

          {/* High-spend / Tail spend cards */}
          <div className="csip-stats-grid">
            <div className="csip-stat-card">
              <div className="csip-stat-label">High-Spend</div>
              <div className="csip-stat-count">{highSpendGroups.length}</div>
              <div className="csip-stat-value">{usd(highSpendSum)}</div>
            </div>
            <div className="csip-stat-card csip-stat-card--tail">
              <div className="csip-stat-label csip-stat-label--tail">Tail (Low-Spend)</div>
              <div className="csip-stat-count csip-stat-count--tail">{tailGroups.length}</div>
              <div className="csip-stat-value csip-stat-value--tail">{usd(tailSum)}</div>
            </div>
          </div>

          {/* Bar chart */}
          <div className="csip-chart-wrap">
            <div className="csip-chart-inner">
              <SpendBarChart data={barData} />
            </div>
          </div>

          {/* Insight */}
          <div className="csip-insight">
            <span className="csip-insight-text">
              <strong>Insight:</strong> {highSpendGroups.length} high-spend suppliers drive {highSpendPct}% of spend({usd(highSpendSum)}); {tailGroups.length} tail suppliers — consolidation opportunity.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
